import { get, has, merge } from 'lodash';
import {
  VEGA_SPEC_AXIS_SETTINGS,
  VEGA_SPEC_LEGEND_SETTINGS,
  CHART_COLORS,
} from 'constants/chart-spec';
import { CHART_CATEGORIES } from 'constants/chart-categories';
import { generateVegaLabelTextExpression } from 'utils/chart/vega-expressions';
import { generateVegaTooltip } from 'utils/chart/tooltips';
import {
  generateVegaSortParams,
  generateVegaTypeCoercionTransforms,
  generateVegaSortingAndLimitingTransforms,
} from 'utils/chart/transforms';
import { getScaleMinMaxProps } from 'utils/chart/scale-min-max';

export default {
  name: 'Grouped Bar',
  displayName: 'Grouped',
  order: 0,
  flavour: 'discrete',
  channels: [
    {
      name: 'x',
      displayName: 'X Axis',
      required: true,
      channelType: 'aggregation',
      intent: 'value-axis',
      multipleSeries: true,
    },
    {
      name: 'y',
      displayName: 'Y Axis',
      required: false,
      channelType: 'category',
      intent: 'category-axis',
    },
    {
      name: 'color',
      displayName: 'Series',
      required: false,
      channelType: 'category',
      intent: 'series',
    },
  ],
  category: CHART_CATEGORIES.BAR,
  iconValue: 'bar-grouped',
  specFn: (encodings, customisations) => {
    const sortParams = generateVegaSortParams(encodings);
    const logScaleOnValueAxis = get(
      customisations.axes,
      'x.logScale.enabled',
      false
    );
    const valueAxisProps = getScaleMinMaxProps(
      customisations,
      'x',
      logScaleOnValueAxis
    );
    return {
      autosize: {
        type: 'fit',
        contains: 'padding',
      },
      style: 'cell',
      data: [
        {
          name: 'transformedData',
          source: 'values',
          transform: [
            ...generateVegaTypeCoercionTransforms('x'),
            ...generateVegaSortingAndLimitingTransforms(encodings),
          ],
        },
      ],
      scales: [
        {
          name: 'x',
          type: logScaleOnValueAxis ? 'log' : 'linear',
          domain: { data: 'transformedData', field: 'x' },
          range: 'width',
          round: true,
          zero: !logScaleOnValueAxis,
          ...valueAxisProps,
        },
        {
          name: 'y',
          type: 'band',
          domain: {
            data: 'transformedData',
            field: 'y',
            sort: sortParams.y,
          },
          range: 'height',
          padding: 0.2,
        },
        {
          name: 'color',
          type: 'ordinal',
          domain: {
            data: 'transformedData',
            field: 'color',
            sort: sortParams.color,
          },
          range:
            get(customisations.options, 'colorDiscrete.value') || CHART_COLORS,
        },
      ],
      axes: [
        merge({}, VEGA_SPEC_AXIS_SETTINGS, {
          orient: 'bottom',
          scale: 'x',
          grid: true,
          title: get(encodings, 'x.axis.title', ''),
          encode: {
            labels: {
              update: {
                text: {
                  signal: generateVegaLabelTextExpression(
                    encodings.x,
                    customisations.channels.x,
                    { withDecimals: false, withSpacePadding: true }
                  ),
                },
              },
            },
          },
        }),
        merge({}, VEGA_SPEC_AXIS_SETTINGS, {
          orient: 'left',
          scale: 'y',
          title: get(encodings, 'y.axis.title', ''),
          encode: {
            labels: {
              update: {
                text: {
                  signal: generateVegaLabelTextExpression(
                    encodings.y,
                    customisations.channels.y
                  ),
                },
              },
            },
          },
        }),
      ],
      legends: !has(encodings, 'color.legend.title')
        ? undefined
        : [
            merge({}, VEGA_SPEC_LEGEND_SETTINGS, {
              fill: 'color',
              title: get(encodings, 'color.legend.title', ''),
              encode: {
                labels: {
                  update: {
                    text: {
                      signal: generateVegaLabelTextExpression(
                        encodings.color,
                        customisations.channels.color
                      ),
                    },
                  },
                },
              },
            }),
          ],
      marks: [
        {
          type: 'group',
          from: {
            facet: {
              data: 'transformedData',
              name: 'facet',
              groupby: 'y',
            },
          },
          encode: {
            enter: {
              y: { scale: 'y', field: 'y' },
            },
          },
          signals: [{ name: 'height', update: "bandwidth('y')" }],
          scales: [
            {
              name: 'pos',
              type: 'band',
              range: 'height',
              domain: {
                data: 'transformedData',
                field: 'color',
                sort: sortParams.color,
              },
            },
          ],
          marks: [
            {
              name: 'bars',
              from: { data: 'facet' },
              type: 'rect',
              encode: {
                enter: {
                  y: { scale: 'pos', field: 'color' },
                  height: { scale: 'pos', band: 1 },
                  x: { scale: 'x', field: 'x' },
                  x2: {
                    scale: 'x',
                    value: logScaleOnValueAxis ? valueAxisProps.domainMin : 0,
                  },
                  fill: { scale: 'color', field: 'color' },
                  tooltip: {
                    signal: generateVegaTooltip(encodings, customisations),
                  },
                },
              },
            },
            ...(!get(customisations.options, 'dataValueLabels.enabled')
              ? []
              : [
                  {
                    type: 'text',
                    name: 'valueLabels',
                    from: { data: 'facet' },
                    encode: {
                      enter: {
                        text: {
                          signal: generateVegaLabelTextExpression(
                            encodings.x,
                            customisations.channels.x,
                            { field: 'x' }
                          ),
                        },
                        y: {
                          scale: 'pos',
                          field: 'color',
                          offset: { signal: "bandwidth('pos') / 2" },
                        },
                        x: { scale: 'x', field: 'x' },
                        baseline: { value: 'middle' },
                        dx: { value: 3 },
                      },
                    },
                  },
                ]),
          ],
        },
      ],
    };
  },
};
